import React, { useState, useCallback } from 'react';
import { Spin } from 'antd';
import { sparkToast } from '@spark';
import AiActionLink from '@/features/datasets/components/AiActionLink';
import AIGeneratedLabelsManager from '@/features/datasets/components/AIGeneratedLabelsManager';
import { TaskType } from '@/shared/domain/taskTypes';
import { data_from_server_post } from '@/shared/http/client';
import { aiLabelRowsToMapping, makeEmptyAiLabelRow } from '@/features/tasks/utils/aiLabels';

/**
 * "Generate labels with AI" block used when the user toggles
 * "Does your data contain labels?" OFF.
 *
 * Owns the suggest-labels fetch lifecycle and renders either:
 *   - the AI action link (initial empty state), or
 *   - `AIGeneratedLabelsManager` once rows exist (generated or user-added).
 *
 * Stateless apart from the in-flight loading flag — the row list is owned by
 * the parent (per-objective entry in multi-objective mode, modal-level in
 * legacy mode) so that the final mapping flows back through the same submit
 * path as manual label review.
 *
 * @param {Object}   props
 * @param {string}   props.clientId
 * @param {string}   props.usecaseId      - LLM usecase id used by the LLM endpoint.
 * @param {string}   props.taskType       - Only `TaskType.classification` is currently supported.
 * @param {Array}    props.aiLabels       - Row list shape: see `utils/task/aiLabels.js`.
 * @param {(rows: Array) => void} props.onChange
 * @param {Array<string>} props.availableLabels
 * @param {() => Array<string>} props.getExamples - Callback returning example strings to seed the LLM.
 * @param {boolean} [props.showRejectionColumn] - Show the single-label "Set as 'Fallback'" fallback column.
 * @param {string|null} [props.rejectionLabelName] - Currently selected fallback label.
 * @param {(name: string|null) => void} [props.onSetRejectionLabel]
 */
const AiGeneratedLabelsBlock = ({
    clientId,
    usecaseId,
    taskType,
    aiLabels = [],
    onChange,
    availableLabels = [],
    getExamples,
    showRejectionColumn = false,
    rejectionLabelName = null,
    onSetRejectionLabel,
}) => {
    const [loading, setLoading] = useState(false);

    const canGenerate = taskType === TaskType.classification;

    const updateRow = useCallback(
        (id, patch) => {
            const next = (aiLabels || []).map((row) =>
                row.id === id ? { ...row, ...patch } : row
            );
            onChange?.(next);
        },
        [aiLabels, onChange]
    );

    const deleteRow = useCallback(
        (id) => {
            const next = (aiLabels || []).filter((row) => row.id !== id);
            onChange?.(next);
        },
        [aiLabels, onChange]
    );

    const addRow = useCallback(() => {
        const next = [
            ...(aiLabels || []),
            makeEmptyAiLabelRow((aiLabels || []).length),
        ];
        onChange?.(next);
    }, [aiLabels, onChange]);

    const handleGenerate = useCallback(async () => {
        if (!canGenerate) {
            sparkToast.error(
                'AI label generation is currently supported for classification tasks only.'
            );
            return;
        }
        if (!clientId || !usecaseId) {
            sparkToast.error('Task not found.');
            return;
        }
        setLoading(true);
        try {
            const examples = typeof getExamples === 'function' ? getExamples() : [];
            const resp = await new Promise((resolve, reject) => {
                data_from_server_post(
                    'ai-generation/suggest-labels',
                    {
                        client_id: clientId,
                        usecase_id: usecaseId,
                        n_labels: 6,
                        examples,
                    },
                    (response) => resolve(response),
                    (error) => reject(new Error(error || 'Request failed'))
                );
            });
            const labels = Array.isArray(resp?.labels) ? resp.labels : [];
            const unique = Array.from(
                new Set(labels.map((l) => (l || '').trim()).filter(Boolean))
            );
            const rows = unique.map((name, idx) => ({
                id: `ai_label_${Date.now()}_${idx}`,
                name,
                replaceWith: name,
                isNew: false,
            }));
            onChange?.(rows.length ? rows : [makeEmptyAiLabelRow(0)]);
        } catch (e) {
            sparkToast.error(e?.message || 'Failed to generate labels');
        } finally {
            setLoading(false);
        }
    }, [canGenerate, clientId, usecaseId, getExamples, onChange]);

    const hasRows = (aiLabels || []).length > 0;

    return (
        <div style={{ marginTop: 25 }}>
            {!hasRows && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 10,
                    }}
                >
                    <AiActionLink
                        label="Generate labels with AI"
                        disabled={!canGenerate || loading}
                        onClick={handleGenerate}
                    />
                    {loading ? <Spin size="small" /> : null}
                </div>
            )}

            {hasRows && (
                <AIGeneratedLabelsManager
                    labels={aiLabels}
                    availableLabels={availableLabels}
                    onChangeNewLabelName={(id, nextName) => updateRow(id, { name: nextName })}
                    onChangeReplaceWith={(id, nextReplace) => {
                        const row = (aiLabels || []).find((r) => r.id === id);
                        const isNew = !!row?.isNew;
                        updateRow(id, {
                            replaceWith: nextReplace,
                            // Lock empty *generated* rows (rare fallback) to selected label.
                            name: isNew
                                ? row?.name
                                : (row?.name ? row.name : (nextReplace || '')),
                        });
                    }}
                    onDeleteLabel={deleteRow}
                    onAddLabel={addRow}
                    showRejectionColumn={showRejectionColumn}
                    rejectionLabelName={rejectionLabelName}
                    onSetRejectionLabel={onSetRejectionLabel}
                />
            )}
        </div>
    );
};

export { aiLabelRowsToMapping };
export default AiGeneratedLabelsBlock;
